import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { UserGroceryStore } from '../types/recipe';

interface GroceryStore {
  stores: UserGroceryStore[];
  defaultStore: UserGroceryStore | null;
  loading: boolean;
  error: string | null;
  fetchStores: () => Promise<void>;
  setDefaultStore: (storeId: string) => Promise<void>;
}

export const useGroceryStore = create<GroceryStore>((set, get) => ({
  stores: [],
  defaultStore: null,
  loading: false,
  error: null,

  fetchStores: async () => {
    set({ loading: true, error: null });
    try {
      const { data: stores, error } = await supabase
        .from('user_grocery_stores')
        .select('*')
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) throw error;

      const defaultStore = stores?.find(store => store.is_default) || stores?.[0] || null;
      set({ 
        stores: stores || [],
        defaultStore
      });
    } catch (err) {
      console.error('Error fetching stores:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to fetch stores' });
    } finally {
      set({ loading: false });
    }
  },

  setDefaultStore: async (storeId: string) => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update the store in the database
      const { error } = await supabase
        .from('user_grocery_stores')
        .update({ is_default: true })
        .eq('store_id', storeId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Refresh the stores list
      await get().fetchStores();
    } catch (err) {
      console.error('Error setting default store:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to set default store' });
    } finally {
      set({ loading: false });
    }
  }
}));